import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { AccessModes, Dimensions, Metrics } from 'components/insights/constants';
import { formatResponseTime, formatWholeNumber } from 'components/insights/formatting';
import { getFormattedDatesInRange } from 'components/shared/charts/helpers';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { useNetworkPerformanceStore } from 'stores/networkPerformanceStore';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../../shared/slideout/MetricDetailLayout';
import { readGrandTotal, readGrandTotalOrZero } from '../../../shared/slideout/rollupHelpers';
import { StatTile } from '../../../shared/slideout/statTiles';
import useNetworkPerformanceAnalytics from '../../query/useNetworkPerformanceAnalytics';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../constants';
import { type MetricHeaderProps } from '../NetworkPerformanceSlideout';

const config = METRIC_CONFIGS[NetworkPerformanceMetrics.SAFE_RETURNS];

// Side query for safe returns over time (DATE dimension for area chart)
const SAFE_RETURNS_OVER_TIME_PARAMS = {
  source: config.source,
  accessModes: [AccessModes.MANAGER],
  dimensions: [Dimensions.DATE],
  metrics: [config.primaryMetric],
  reviewType: config.reviewType,
};

export default function SafeReturnsHeader({ loading, rollups, highestEntity }: MetricHeaderProps) {
  const filters = useNetworkPerformanceStore((s) => s.filters);
  const safeReturnsRequested = readGrandTotalOrZero(rollups, Metrics.SAFE_RETURNS_REQUESTED_COUNT);
  const avgResponseTime = readGrandTotal(rollups, Metrics.SAFE_RETURN_RESPONSE_TIME_AVERAGE);

  // Side query for time series data
  const { data: timeData, loading: timeLoading } = useNetworkPerformanceAnalytics(SAFE_RETURNS_OVER_TIME_PARAMS, {
    queryKey: 'networkPerformanceSlideout:safeReturns:overTime',
  });

  const chartData = useMemo(() => {
    // Fill every date in the range with 0, then overlay actual values.
    // This ensures consistent axis spacing and shows zeroes on gap days.
    const allDates = getFormattedDatesInRange(filters[START_DATE] as Date, filters[END_DATE] as Date);
    const dateMap: Record<string, number> = {};
    for (const d of allDates) dateMap[d] = 0;

    const apiDates = timeData.dimensions.getCurrentDimensionValuesByIndex(0);
    const counts = timeData.metrics.getCurrentSeries(Metrics.SAFE_RETURNS_REQUESTED_COUNT);
    apiDates.forEach((date, i) => {
      dateMap[date] = counts[i] ?? 0;
    });

    return Object.entries(dateMap).map(([date, count]) => ({ date, count }));
  }, [timeData, filters]);

  const chartOptions = useMemo<Highcharts.Options>(() => {
    return {
      chart: {
        type: 'area',
        style: { fontFamily: 'Lato' },
        height: 200,
      },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 6,
        itemStyle: { fontSize: '12px', fontWeight: 'normal', color: colors.black75 },
      },
      xAxis: {
        categories: chartData.map((d) => {
          const [, month, day] = d.date.split('-');
          return `${parseInt(month, 10)}/${parseInt(day, 10)}`;
        }),
        lineColor: colors.black10,
        tickLength: 0,
        labels: {
          style: { fontSize: '10px', color: colors.black50 },
          step: Math.max(1, Math.floor(chartData.length / 10)),
        },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.black10,
        labels: { style: { fontSize: '10px', color: colors.black50 } },
        min: 0,
      },
      tooltip: {
        backgroundColor: colors.chartTooltipBackground,
        style: { color: colors.white },
        padding: 12,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          return `${this.x}: ${this.y} ${config.primaryMetricLabel}`;
        },
      },
      plotOptions: {
        area: {
          marker: {
            enabled: true,
            radius: 4,
            fillColor: colors.white,
            lineColor: colors.accentGreen,
            lineWidth: 2,
          },
          color: colors.accentGreen,
          fillColor: colors.accentGreen10,
          connectNulls: true,
        },
        series: { animation: false },
      },
      series: [
        {
          type: 'area',
          name: config.primaryMetricLabel,
          data: chartData.map((d) => ({
            y: d.count,
            marker: { enabled: d.count > 0 },
          })),
        },
      ],
    };
  }, [chartData]);

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label={config.primaryMetricLabel}
            loading={loading}
            primary={formatWholeNumber(safeReturnsRequested)}
          />
          <StatTile
            label='Avg. Safe Return Response Time'
            loading={loading}
            primary={formatResponseTime(avgResponseTime, { compact: true })}
            addendum='Avg. time from request to acknowledgement'
          />
          <StatTile
            label='Most Safe Returns'
            loading={loading}
            primary={highestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={highestEntity ? `${highestEntity.value} safe returns requested` : undefined}
            tone='green'
          />
        </>
      }
      chartTitle={`${config.primaryMetricLabel} Over Time Period`}
      chartLoading={loading || timeLoading}
      isEmpty={chartData.length === 0}
      emptyMessage='No safe returns to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
